import type { ConversationListItem } from '@/shared/api';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { formatRelative } from '@/shared/lib/format';
import { awaitingReply, channelLabel, classificationLabel, waitingSince } from '../inboxModel';
import { threadDisplayName } from '../threadDisplayName';

const CHANNEL_DOT: Record<string, string> = {
  Airbnb: 'bg-airbnb',
  BookingCom: 'bg-booking-com',
};

/** Eine Unterhaltung in der Liste: wer, über welchen Kanal, was zuletzt, und ob sie wartet. */
export function ThreadRow({
  thread,
  selected,
  now,
  onSelect,
}: {
  thread: ConversationListItem;
  selected: boolean;
  now: Date;
  onSelect: () => void;
}) {
  const { t } = useTranslation('conversationResolution');
  const name = threadDisplayName(thread.guestName, thread.subject) ?? 'Unterhaltung';
  const unread = thread.unreadCount > 0;
  const waiting = awaitingReply(thread) ? waitingSince(thread.lastMessageReceivedAt, now) : null;
  const tag = classificationLabel(thread.classification);
  const when = thread.lastMessageReceivedAt ?? thread.lastMessageSentAt;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'rounded-control focus-visible:ring-sage flex min-h-11 w-full gap-3 px-3 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2',
        selected ? 'bg-forest/10 shadow-[inset_3px_0_0_var(--color-forest)]' : 'hover:bg-card-alt',
      )}
    >
      <span className="bg-ink-soft relative grid size-10 flex-none place-items-center rounded-full text-[13px] font-bold text-white">
        {name[0]?.toUpperCase() ?? '?'}
        <span
          aria-hidden
          className={cn(
            'absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full border-2 border-white',
            CHANNEL_DOT[thread.channel] ?? 'bg-ink-faint',
          )}
        />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'text-ink min-w-0 flex-1 truncate text-sm',
              unread ? 'font-extrabold' : 'font-semibold',
            )}
          >
            {name}
          </span>
          {unread && (
            <span className="bg-sealwax size-2 flex-none rounded-full" aria-label="ungelesen" />
          )}
          <span className="text-ink-faint flex-none font-mono text-[11px]">
            {when ? formatRelative(when) : ''}
          </span>
        </span>
        <span className="text-ink-soft truncate text-xs">
          {channelLabel(thread.channel)} · {thread.propertyName ?? 'kein Apartment'}
        </span>
        {thread.lastMessagePreview && (
          <span
            className={cn(
              'truncate text-[13px]',
              unread ? 'text-ink font-semibold' : 'text-ink-soft',
            )}
          >
            {thread.lastMessagePreview}
          </span>
        )}
        {(waiting || tag || thread.isClosed || thread.isLocallyResolved) && (
          <span className="flex flex-wrap items-center gap-1.5 pt-0.5">
            {waiting && <span className="text-sealwax text-xs font-bold">{waiting}</span>}
            {tag && (
              <span className="rounded-pill bg-amber-tint text-amber-ink px-2 py-0.5 text-[11px] font-bold">
                {tag}
              </span>
            )}
            {thread.isClosed && (
              <span className="rounded-pill bg-card-alt text-ink-soft px-2 py-0.5 text-[11px] font-bold">
                Geschlossen
              </span>
            )}
            {thread.isLocallyResolved && (
              <span className="rounded-pill bg-card-alt text-ink-soft px-2 py-0.5 text-[11px] font-bold">
                {t('resolved')}
              </span>
            )}
          </span>
        )}
      </span>
    </button>
  );
}
